import { ClockCircleOutlined,DollarOutlined,EnvironmentOutlined,SearchOutlined,TeamOutlined } from '@ant-design/icons';
import { Input,Pagination,Select,Tag } from "antd";
import { getJobListPublicApi } from "api/services/jobs";
import { Button } from 'components/Button';
import { SkeltonLoader } from "components/skeltonLoader";
import { isValid,parse,parseISO } from 'date-fns';
import { useCallback,useEffect,useMemo,useState } from "react";
import { useNavigate,useSearchParams } from 'react-router-dom';
import { dateLeftHandler,formatDateHandler2 } from "utils/common";
import { defaultPagination,itemRender,pageSizeOptions } from "utils/table";

const Jobs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [sortOption, setSortOption] = useState("newest");
  const [pageSettings , setPageSettings] = useState({
    ...defaultPagination,
    page: searchParams.get("page") || 1,
    pageSize: parseInt(searchParams.get("size")) || 50,
    sortType: searchParams.get("sort_type") || "",
    sortBy: searchParams.get("sort_by") || "",
  });
  
  const [jobList, setJobData] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Parse date with multiple format support
  const parseExpiryDate = useCallback((dateString) => {
    if (!dateString) return null;
    
    // Try ISO format first
    try {
      const isoDate = parseISO(dateString);
      if (isValid(isoDate)) return isoDate;
    } catch (e) {}
    
    // Try MM/dd/yyyy format
    try {
      const parsedDate = parse(dateString, 'MM/dd/yyyy', new Date());
      if (isValid(parsedDate)) return parsedDate;
    } catch (e) {}
    
    // Try dd/MM/yyyy format
    try {
      const parsedDate = parse(dateString, 'dd/MM/yyyy', new Date());
      if (isValid(parsedDate)) return parsedDate;
    } catch (e) {}
    
    // Try standard Date constructor as fallback
    try {
      const date = new Date(dateString);
      if (isValid(date)) return date;
    } catch (e) {}
    
    return null;
  }, []);

  // Memoized function to calculate time left for any target date (never returns negative)
  const calculateTimeLeft = useCallback((targetDate) => {
    if (!targetDate) return null;
    
    const expiryDate = parseExpiryDate(targetDate);
    if (!expiryDate) return null;
    
    // Compare dates - set time to start of day for accurate comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    
    // If expired, return null
    if (expiry.getTime() < today.getTime()) {
      return null;
    }
    
    return dateLeftHandler(targetDate);
  }, [parseExpiryDate]);

  // Check if job is expired
  const isJobExpired = useCallback((expiryDate) => {
    if (!expiryDate) return false;
    
    const parsedDate = parseExpiryDate(expiryDate);
    if (!parsedDate) return false;
    
    // Compare dates - set time to start of day for accurate comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(parsedDate);
    expiry.setHours(0, 0, 0, 0);
    
    return expiry.getTime() < today.getTime();
  }, [parseExpiryDate]); 

  const getJobListPublicHandler = () => {
    setLoading(true)
    getJobListPublicApi({
      sort_type: pageSettings.sortType,
      sort_by: pageSettings.sortBy
    }).then(response=>{
      console.log(response.data.results)
      setJobData(response.data.results);
      
      // Extract unique departments for filter
      const uniqueDepartments = [...new Set(response.data.results.map(job => 
        job.department?.name
      ))].filter(Boolean);
      setDepartments(uniqueDepartments);
      
      setPageSettings({
        ...pageSettings,
        total: response.data.count
      })
  
      setLoading(false)
    }).catch(e=>{
      setLoading(false)
    })
  }

  useEffect(() => {
    getJobListPublicHandler()
  }, [pageSettings?.page, pageSettings?.pageSize, pageSettings?.sortType, pageSettings?.sortBy]);

  const handleGridChange = (page, size) => {
    setPageSettings({
      ...pageSettings,
      pageSize: size,
      page,
    })
  }

  const handleSortChange = (value) => {
    setSortOption(value);
    let sortType = "";
    let sortBy = "";
    
    if (value === "newest") {
      sortType = "desc";
      sortBy = "created_at";
    } else if (value === "oldest") {
      sortType = "asc";
      sortBy = "created_at";
    } else if (value === "alphabetical") {
      sortType = "asc";
      sortBy = "title";
    }
    
    setPageSettings({
      ...pageSettings,
      sortType,
      sortBy,
      page: 1 // Reset to first page when sorting changes
    });
  }

  const navigateToDetail = (item) => {
    navigate(`/job-view/${item.slug}`)
  }
  
  // Filter and sort jobs
  const filteredJobs = useMemo(() => {
    // First filter the jobs
    let filtered = jobList.filter(job => {
      const matchesSearch = job?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          job?.department?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDepartment = selectedDepartment === "all" || 
                              job?.department?.name === selectedDepartment;
      return matchesSearch && matchesDepartment;
    });
    
    // Then sort them client-side if needed (as backup in case API sorting fails)
    if (pageSettings.sortBy && pageSettings.sortType && filtered.length > 0) {
      const sortBy = pageSettings.sortBy;
      const sortType = pageSettings.sortType;
      
      filtered.sort((a, b) => {
        let valueA = a[sortBy] || '';
        let valueB = b[sortBy] || '';
        
        // Handle nested properties like department.name
        if (sortBy.includes('.')) {
          const parts = sortBy.split('.');
          valueA = parts.reduce((obj, key) => obj?.[key], a) || '';
          valueB = parts.reduce((obj, key) => obj?.[key], b) || '';
        }
        
        // Handle date comparison
        if (sortBy === 'created_at' || sortBy === 'expiry_date') {
          valueA = new Date(valueA).getTime();
          valueB = new Date(valueB).getTime();
        }
        
        if (sortType === 'asc') {
          return valueA > valueB ? 1 : -1;
        } else {
          return valueA < valueB ? 1 : -1;
        }
      });
    }
    
    return filtered;
  }, [jobList, searchTerm, selectedDepartment, pageSettings.sortBy, pageSettings.sortType]);

  const GridView = () => {
    if (loading) {
      return (
        <SkeltonLoader
          number={pageSettings?.pageSize}
          active={true}
          className="col-xl-4 col-lg-6 col-md-6"
        />
      );
    } else if (filteredJobs.length === 0) {
      return (
        <div className="col-12 text-center py-5">
          <div className="empty-state">
            <i className="fa fa-search fa-3x text-muted mb-3"></i>
            <h3>No positions available at this time</h3>
            <p className="text-muted">Check back soon for new opportunities at Nando's!</p>
          </div>
        </div>
      );
    } else {
      return (
        <>
          {filteredJobs.map((job, index) => (
            <div className="col-xl-4 col-lg-6 col-md-6 mb-4" key={job.id}>
              <div className="card job-card h-100 shadow-sm hover-shadow">
                <div className="nandos-card-accent"></div>
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="company-logo me-3">
                      <div className="nandos-logo-container rounded d-flex align-items-center justify-content-center">
                        {/* <FireOutlined className="nandos-icon" /> */}
                        <img src={require("../../../../../../assets/img/logo3.png")} alt="Nando's" className="img-fluid contain" style={{ height: 50,}} />
                      </div>
                    </div>
                    <div>
                      <h5 className="job-title fw-bold mb-1">{job?.title}</h5>
                      <p className="company-name mb-0">Nando's</p>
                    </div>
                  </div>
                  
                  <div className="job-details mb-3">
                    <div className="d-flex flex-wrap gap-3 mb-3">
                      {job?.location && (
                        <div className="d-flex align-items-center">
                          <EnvironmentOutlined className="me-1 nandos-text" />
                          <small>{job.location}</small>
                        </div>
                      )}
                      <div className="d-flex align-items-center">
                        <TeamOutlined className="me-1 nandos-text" />
                        <small>Full time</small>
                      </div>
                      {job?.salary_range && (
                        <div className="d-flex align-items-center">
                          <DollarOutlined className="me-1 nandos-text" />
                          <small>{job.salary_range}</small>
                        </div>
                      )}
                    </div>
                    
                    <p className="job-description mb-3" style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: "2",
                      WebkitBoxOrient: "vertical",
                      minHeight: "48px"
                    }}>
                      {job?.description || "Join our Nando's family and be part of our passionate team delivering PERi-PERi goodness and exceptional customer experiences."}
                    </p>
                    
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <Tag color="#a61022">{job?.department?.name}</Tag>
                      {job?.skills?.slice(0, 2).map((skill, i) => (
                        <Tag key={i} color="#4a8501">{skill}</Tag>
                      ))}
                    </div>
                  </div>
                  
                  <div className="d-flex justify-content-between align-items-center mt-auto">
                    {job?.expiry_date && (
                      <div className="deadline">
                        <div className="d-flex align-items-center">
                          <ClockCircleOutlined className="me-1" style={{ color: isJobExpired(job.expiry_date) ? '#dc3545' : '#a61022' }} />
                          <small className={`fw-medium ${isJobExpired(job.expiry_date) ? 'text-danger' : 'nandos-text'}`}>
                            {isJobExpired(job.expiry_date) 
                              ? "Application Closed" 
                              : (calculateTimeLeft(job?.expiry_date) ? `${calculateTimeLeft(job?.expiry_date)} left` : "Closing soon")
                            }
                          </small>
                        </div>
                        <small className="d-block mt-1">Posted: {formatDateHandler2(job?.created_at || new Date())}</small>
                      </div>
                    )}
                    
                    {!isJobExpired(job?.expiry_date) && (
                      <Button
                        label="Apply Now"
                        className="btn nandos-btn"
                        onClick={() => navigateToDetail(job)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      );
    }
  };

  return (
    <div>
      <div className="main-wrapper">
        <div className="page-wrapper job-wrapper">
          {/* Hero Header Section - Full Width */}
          <div className="nandos-hero-header">
            <div className="container">
              <div className="row">
                <div className="col-lg-8 mx-auto text-center">
                  <div className="nandos-logo-large">
                    <img src={require("../../../../../../assets/img/logo4.png")} alt="Nando's" className="img-fluid nandos-header-logo" />
                  </div>
                  <h1 className="nandos-header-title text-white">Join the Nando's Family</h1>
                  <p className="nandos-header-subtitle text-white">Discover exciting career opportunities with our passionate team</p>
                  
                  <div className="search-box bg-white p-2 shadow">
                    <div className="input-group">
                      <span className="input-group-text bg-transparent border-0">
                        <SearchOutlined className="search-icon" />
                      </span>
                      <Input 
                        placeholder="Search for positions at Nando's" 
                        variant="borderless"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="form-control border-0 shadow-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="content container">
            {/* Filter Section */}
            <div className="job-filter mb-4">
              <div className="row">
                <div className="col-12 col-md-6 mb-3 mb-md-0">
                  <h4 className="job-filter-title fw-bold">Available Positions</h4>
                  <p className="job-filter-subtitle text-muted mb-0">Find your perfect role at Nando's</p>
                </div>
                <div className="col-12 col-md-6">
                  <div className="job-filter-controls">
                    <div className="filter-item">
                      <label className="filter-label">Department</label>
                      <Select
                        defaultValue="all"
                        className="filter-select"
                        onChange={(value) => setSelectedDepartment(value)}
                        options={[
                          { value: 'all', label: 'All Departments' },
                          ...departments.map(dept => ({ value: dept, label: dept }))
                        ]}
                      />
                    </div>
                    <div className="filter-item">
                      <label className="filter-label">Sort by</label>
                      <Select
                        value={sortOption}
                        className="filter-select"
                        options={[
                          { value: 'newest', label: 'Newest' },
                          { value: 'oldest', label: 'Oldest' },
                          { value: 'alphabetical', label: 'Alphabetical' }
                        ]}
                        onChange={handleSortChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Job Listings */}
            <div className="job-listings">
              <div className="row">
                <GridView/>
              </div>
            </div>
            
            {/* Pagination */}
            <div className="my-4 d-flex justify-content-center">
              <Pagination
                pageSize={pageSettings?.pageSize}
                pageSizeOptions={pageSizeOptions}
                defaultCurrent={1}
                current={pageSettings?.page}
                total={pageSettings?.total}
                itemRender={itemRender}
                onChange={handleGridChange}
                showSizeChanger={true}
                showQuickJumper={false}
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} jobs`}
              />
            </div>

            {/* Nando's Values Section */}
            {/* <div className="nandos-values-section p-4 p-md-5 rounded-3 mt-5 text-center">
              <h3 className="mb-4">Why Work at Nando's?</h3>
              <div className="row g-4">
                <div className="col-md-4">
                  <div className="value-card p-3">
                    <FireOutlined className="value-icon mb-3" />
                    <h5>Passion</h5>
                    <p>We're passionate about our food, our people, and creating memorable experiences.</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="value-card p-3">
                    <TeamOutlined className="value-icon mb-3" />
                    <h5>Family</h5>
                    <p>Join our diverse team where everyone belongs and grows together.</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="value-card p-3">
                    <i className="fa fa-heart value-icon mb-3"></i>
                    <h5>Pride</h5>
                    <p>Take pride in delivering exceptional experiences every day.</p>
                  </div>
                </div>
              </div>
            </div> */}


            <div className="nandos-footer p-4 mt-5 text-center">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <img src={require("../../../../../../assets/img/logo3.png")} alt="Nando's" className="img-fluid mb-3" style={{ maxHeight: '60px' }} />
                  <p className="mb-0">© {new Date().getFullYear()} Nando's. All rights reserved.</p>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
      
      {/* Add custom CSS */}
      <style>{`
        .job-wrapper {
          background-color: #f8f9fa;
          min-height: 100vh;
          padding: 20px 0;
        }
        
        .job-filter {
          background: #ffffff;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .job-filter-title {
          color: #333;
          font-size: 24px;
          margin-bottom: 4px;
        }
        
        .job-filter-subtitle {
          font-size: 14px;
        }
        
        .job-filter-controls {
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
          align-items: center;
          gap: 16px;
        }
        
        .filter-item {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .filter-label {
          font-size: 13px;
          font-weight: 500;
          color: #666;
          margin-bottom: 0;
        }
        
        .filter-select {
          min-width: 140px;
        }
        
        .filter-select .ant-select-selector {
          border-radius: 8px !important;
        }
        
        /* Mobile Responsive Filter */
        @media (max-width: 768px) {
          .job-filter {
            padding: 16px;
            border-radius: 8px;
          }
          
          .job-filter-title {
            font-size: 20px;
            margin-bottom: 2px;
          }
          
          .job-filter-subtitle {
            font-size: 13px;
            margin-bottom: 16px;
          }
          
          .job-filter-controls {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
            width: 100%;
          }
          
          .filter-item {
            width: 100%;
          }
          
          .filter-label {
            font-size: 12px;
            margin-bottom: 4px;
          }
          
          .filter-select {
            width: 100% !important;
            min-width: 100%;
          }
        }
        
        .job-listings {
          margin-top: 20px;
        }
        
        .ant-select-selector {
          border-radius: 8px !important;
        }
        
        .job-card {
          transition: all 0.3s ease;
          border: none;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
          border-radius: 12px;
          background: #ffffff;
        }
        
        .nandos-card-accent {
          height: 5px;
          background: linear-gradient(90deg, #a61022 0%, #f8c429 100%);
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
        }
        
        .hover-shadow:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        
        .job-wrapper {
          padding-top: 0 !important;
        }
        
        .nandos-hero-header {
          background: linear-gradient(135deg, #a61022 0%, #7a0c19 100%);
          color: white;
          width: 100%;
          padding: 30px 0;
          margin: 0;
          margin-top: 0 !important;
          box-shadow: 0 4px 20px rgba(166, 16, 34, 0.2);
        }
        
        .nandos-logo-large {
          margin-bottom: 16px;
        }
        
        .nandos-header-logo {
          max-height: 60px;
        }
        
        .nandos-header-title {
          font-size: 28px;
          font-weight: 400;
          margin-bottom: 8px;
          line-height: 1.2;
        }
        
        .nandos-header-subtitle {
          font-size: 14px;
          margin-bottom: 20px;
          opacity: 0.95;
        }
        
        .search-box {
          max-width: 600px;
          margin: 0 auto;
          border-radius: 8px !important;
        }
        
        .search-box .ant-input {
          border-radius: 8px !important;
        }
        
        .search-box .input-group {
          border-radius: 8px !important;
        }
        
        .search-box .input-group-text {
          border-radius: 8px 0 0 8px !important;
          padding-left: 12px;
        }
        
        .search-icon {
          font-size: 16px;
          color: #666;
        }
        
        .nandos-hero {
          background: linear-gradient(135deg, #a61022 0%, #7a0c19 100%);
          color: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(166, 16, 34, 0.2);
        }
        
        /* Mobile Responsive Header */
        @media (max-width: 768px) {
          .nandos-hero-header {
            padding: 20px 0;
          }
          
          .nandos-logo-large {
            margin-bottom: 12px;
          }
          
          .nandos-header-logo {
            max-height: 45px;
          }
          
          .nandos-header-title {
            font-size: 22px;
            margin-bottom: 6px;
          }
          
          .nandos-header-subtitle {
            font-size: 13px;
            margin-bottom: 16px;
            padding: 0 10px;
          }
          
          .search-box {
            margin: 0 10px;
          }
          
          .search-box .input-group-text {
            padding-left: 10px;
          }
        }
        
        .nandos-logo-container {
          width: 50px;
          height: 50px;
          background-color: #a61022;
          color: #fff;
        }
        
        .nandos-icon {
          font-size: 24px;
          color: #f8c429;
        }
        
        .job-title {
          color: #333;
          font-size: 18px;
          line-height: 1.4;
        }
        
        .company-name {
          color: #a61022;
          font-weight: 500;
        }
        
        .nandos-text {
          color: #a61022;
        }
        
        .nandos-btn {
          background-color: #a61022;
          border-color: #a61022;
          color: white;
        }
        
        .nandos-btn:hover {
          background-color: #7a0c19;
          border-color: #7a0c19;
          color: white;
        }
        
        .empty-state {
          padding: 40px 0;
        }
        
        .nandos-values-section {
          background-color: #fff;
          border: 1px solid #eaeaea;
        }
        
        .value-card {
          height: 100%;
          background-color: #f8f9fa;
          border-radius: 8px;
        }
        
        .value-icon {
          font-size: 32px;
          color: #a61022;
        }
           .nandos-footer {
          background-color: #333;
          color: white;
        }
      `}</style>
    </div>
  );
};

export default Jobs;
